// eslint-disable-next-line unicorn/prefer-node-protocol
import { ParsedUrlQueryInput } from 'node:querystring';

// eslint-disable-next-line shared-rules/base-imports
import { formatUrl } from 'next/dist/shared/lib/router/utils/format-url';

import queryString from 'query-string';

import {
  accountsAbsoluteBaseUrl,
  cloudAbsoluteBaseUrl,
  docsAbsoluteBaseUrl,
  hubAbsoluteBaseUrl,
  websiteAbsoluteBaseUrl,
} from '../constants/url';

/**
 * According to the documentation, in order to perform "shallow" redirect,
 * the page must stay the same and only params can change. However, since
 * we use [name] syntax for the paths, NextJS considers this as a different
 * page and ignores "shallow" parameter. This function simply replaces dynamic
 * parameters in the path with values which we pass in "query" parameter.
 */
export function generateUrl({
  hostname,
  includeHost,
  ...urlObj
}: Parameters<typeof formatUrl>[0] & {
  hash?: string;
  includeHost?: boolean;
  pathname: string;
  query?: ParsedUrlQueryInput;
}) {
  if (urlObj.query && typeof urlObj.query === 'object' && urlObj.pathname) {
    for (const key of Object.keys(urlObj.query)) {
      if (urlObj.pathname.includes(`[${key}]`) && urlObj.query[key]) {
        urlObj.pathname = urlObj.pathname.replace(`[${key}]`, `${urlObj.query[key]}`);
        delete urlObj.query[key];
      }
    }
  }

  const url = formatUrl({
    ...urlObj,
    query: queryString.stringify(urlObj.query as Record<string, string>, {
      arrayFormat: 'comma',
    }),
  });

  return includeHost || hostname ? `${hostname || getAbsoluteBaseUrl()}${url}` : url;
}

export function getAbsoluteBaseUrl() {
  if (process.env.VERCEL_ENV === 'preview') {
    return `https://${process.env.VERCEL_URL}`;
  }

  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NEXT_PUBLIC_PLAYWRIGHT_TEST === 'true'
  ) {
    return process.env.APP_NAME === 'platform'
      ? 'http://localhost:3000'
      : 'https://local.cloudquery.io:8443';
  }

  switch (process.env.APP_NAME) {
    case 'accounts': {
      return accountsAbsoluteBaseUrl;
    }
    case 'cloud': {
      return cloudAbsoluteBaseUrl;
    }
    case 'docs': {
      return docsAbsoluteBaseUrl;
    }
    case 'hub': {
      return hubAbsoluteBaseUrl;
    }
    case 'website': {
      return websiteAbsoluteBaseUrl;
    }
    case 'platform': {
      return typeof window === 'undefined' ? '' : window.location.origin;
    }
  }
}

export function getAbsoluteBaseUrlSubdomain() {
  const baseUrl = getAbsoluteBaseUrl() || '';

  const { hostname } = new URL(baseUrl);

  return hostname.split('.').length > 2 ? hostname.split('.')[0] : undefined;
}

export function getReturnToUrl(returnTo: string | null | undefined) {
  if (!returnTo) {
    return null;
  }

  if (!returnTo.startsWith('/')) {
    try {
      const url = new URL(returnTo);
      if (!url.hostname.endsWith('cloudquery.io')) {
        return null;
      }
    } catch {
      return null;
    }
  }

  return returnTo;
}

export const isAbsoluteLink = (href: string) =>
  href?.startsWith('https://') || href?.startsWith('http://');

export const isAnchorLink = (href: string) => href?.startsWith('#');

export function getCurrentBaseUrl() {
  const { protocol } = window.location;
  const { hostname } = window.location;
  const { port } = window.location;

  let baseUrl = `${protocol}//${hostname}`;
  if (port) {
    baseUrl += `:${port}`;
  }

  return baseUrl;
}

export function isSameUrl(target: URL, current: URL) {
  const targetUrl = new URL(
    `${target.protocol}//${target.host}${target.pathname}?page=${target.searchParams.get('page')}`,
  );
  const currentUrl = new URL(
    `${current.protocol}//${current.host}${current.pathname}?page=${current.searchParams.get(
      'page',
    )}`,
  );

  return targetUrl.href === currentUrl.href;
}
